// Pure builder rules, table maths, dates and labels for the pivot report (ported from Visit's layout.ts).
import { monthName } from '../../lib/i18n/format'
import type { StaffKey } from '../../lib/staffText'
import type { CellValue, DateFilter, DateGroup, DatePreset, MeasureKind, PivotFields, PivotSpec, Zone } from './types'

export type { Zone }

export const LIMITS: Record<Zone, number> = { rows: 4, cols: 2, vals: 8 }

export type AddResult = { layout: PivotSpec; error?: undefined } | { layout?: undefined; error: StaffKey }

export const EMPTY_SPEC: PivotSpec = { rows: [], cols: [], vals: [], dateGroup: 'month', keep: {}, excl: {}, date: { preset: 'last_30' } }

/** Puts `key` into `zone` at `at` (end by default), taking it out of any zone it was in. */
export function addField(layout: PivotSpec, key: string, zone: Zone, isMeasure: boolean, at?: number | null): AddResult {
  if (zone === 'vals' && !isMeasure) return { error: 'pivot.measuresOnly' }
  if (zone !== 'vals' && isMeasure) return { error: 'pivot.dimsOnly' }
  const next: PivotSpec = { ...layout, rows: layout.rows.filter((k) => k !== key), cols: layout.cols.filter((k) => k !== key), vals: layout.vals.filter((k) => k !== key) }
  const list = [...next[zone]]
  if (list.length >= LIMITS[zone]) return { error: zone === 'vals' ? 'pivot.tooManyValues' : 'pivot.tooManyFields' }
  const index = at === null || at === undefined || at > list.length ? list.length : Math.max(0, at)
  list.splice(index, 0, key)
  next[zone] = list
  return { layout: next }
}

/** Removes a placed field and clears its filters. */
export function removeField(layout: PivotSpec, key: string): PivotSpec {
  const keep = Object.fromEntries(Object.entries(layout.keep).filter(([k]) => k !== key))
  const excl = Object.fromEntries(Object.entries(layout.excl).filter(([k]) => k !== key))
  return { ...layout, rows: layout.rows.filter((k) => k !== key), cols: layout.cols.filter((k) => k !== key), vals: layout.vals.filter((k) => k !== key), keep, excl }
}

export function usedFields(layout: PivotSpec): Set<string> {
  return new Set([...layout.rows, ...layout.cols, ...layout.vals])
}

export function zoneOf(layout: PivotSpec, key: string): Zone | null {
  return layout.rows.includes(key) ? 'rows' : layout.cols.includes(key) ? 'cols' : layout.vals.includes(key) ? 'vals' : null
}

export type Move = 'rows' | 'cols' | 'up' | 'down'

/** The moves a placed field's menu offers: the other dimension zone, then up/down within its own. */
export function movesFor(layout: PivotSpec, key: string): Move[] {
  const zone = zoneOf(layout, key)
  if (!zone) return []
  const i = layout[zone].indexOf(key)
  const moves: Move[] = zone === 'rows' ? ['cols'] : zone === 'cols' ? ['rows'] : []
  if (i > 0) moves.push('up')
  if (i < layout[zone].length - 1) moves.push('down')
  return moves
}

/** One menu move, through the same rules as a drop. */
export function moveField(layout: PivotSpec, key: string, move: Move, isMeasure: boolean): AddResult {
  const zone = zoneOf(layout, key)
  if (!zone) return { layout }
  if (move === 'rows' || move === 'cols') return addField(layout, key, move, isMeasure)
  const i = layout[zone].indexOf(key)
  return addField(layout, key, zone, isMeasure, move === 'up' ? i - 1 : i + 1)
}

/** A filter map without `key`, or with it set to values when there are any: an empty list is never sent. */
function withList(m: Record<string, string[]>, key: string, values: string[]): Record<string, string[]> {
  const rest = Object.fromEntries(Object.entries(m).filter(([k]) => k !== key))
  return values.length ? { ...rest, [key]: values } : rest
}

export function setKeep(layout: PivotSpec, key: string, values: string[]): PivotSpec {
  return { ...layout, keep: withList(layout.keep, key, values) }
}

export function addExclude(layout: PivotSpec, key: string, value: string): PivotSpec {
  const current = layout.excl[key] ?? []
  return { ...layout, excl: { ...layout.excl, [key]: current.includes(value) ? current : [...current, value] } }
}

export function removeExclude(layout: PivotSpec, key: string, value: string): PivotSpec {
  return { ...layout, excl: withList(layout.excl, key, (layout.excl[key] ?? []).filter((v) => v !== value)) }
}

export function clearFieldFilter(layout: PivotSpec, key: string): PivotSpec {
  return { ...layout, keep: withList(layout.keep, key, []), excl: withList(layout.excl, key, []) }
}

export function clearFilters(layout: PivotSpec): PivotSpec {
  return { ...layout, keep: {}, excl: {} }
}

export function isFiltered(layout: PivotSpec, key: string): boolean {
  return (layout.keep[key]?.length ?? 0) > 0 || (layout.excl[key]?.length ?? 0) > 0
}

export function filterCount(layout: PivotSpec, key: string): number {
  return (layout.keep[key]?.length ?? 0) + (layout.excl[key]?.length ?? 0)
}

export function setDateGroup(layout: PivotSpec, group: DateGroup): PivotSpec {
  // A new grouping makes the old date values meaningless.
  return { ...clearFieldFilter(layout, 'date'), dateGroup: group }
}

export interface FilterChip {
  key: string
  kind: 'keep' | 'excl'
  value?: string
  label: string
  values?: string[]
}

/** One chip per keep filter, one per excluded value. */
export function filterChips(layout: PivotSpec, label: (key: string) => string): FilterChip[] {
  const chips: FilterChip[] = []
  for (const [key, values] of Object.entries(layout.keep)) {
    if (values.length) chips.push({ key, kind: 'keep', label: label(key), values })
  }
  for (const [key, values] of Object.entries(layout.excl)) for (const value of values) chips.push({ key, kind: 'excl', value, label: label(key) })
  return chips
}

export function activeFilterCount(layout: PivotSpec): number {
  return Object.values(layout.keep).filter((v) => v.length > 0).length + Object.values(layout.excl).reduce((sum, v) => sum + v.length, 0)
}

/** Marks and outcomes are switched off product-wide: never offered, whatever the server lists. */
export const OFF_FIELDS: ReadonlySet<string> = new Set(['outcome', 'mark'])

export function offeredFields(f: PivotFields): PivotFields {
  return { ...f, dimensions: f.dimensions.filter((d) => !OFF_FIELDS.has(d.key)), measures: f.measures.filter((m) => !OFF_FIELDS.has(m.key)) }
}

/** Drops fields the server no longer offers. Returns the keys dropped. */
export function pruneLayout(layout: PivotSpec, known: Set<string>): { layout: PivotSpec; dropped: string[] } {
  const dropped = [...usedFields(layout)].filter((k) => !known.has(k))
  let next = layout
  for (const key of dropped) next = removeField(next, key)
  return { layout: next, dropped }
}

/** A stored layout (older or partial JSON) in the full shape. */
export function normaliseLayout(raw: Partial<PivotSpec> | null | undefined): PivotSpec {
  const lists = (m: Record<string, string[]> | undefined) => Object.fromEntries(Object.entries(m ?? {}).filter(([, v]) => v.length > 0))
  return {
    rows: raw?.rows ?? [],
    cols: raw?.cols ?? [],
    vals: raw?.vals ?? [],
    dateGroup: raw?.dateGroup ?? 'month',
    keep: lists(raw?.keep),
    excl: lists(raw?.excl),
    date: raw?.date ?? { preset: 'last_30' },
  }
}

/** Something to run: a field in Rows or Columns, or a value. */
export function hasFields(layout: PivotSpec): boolean {
  return layout.rows.length + layout.cols.length + layout.vals.length > 0
}

/** Empty filter lists do not make a layout different. */
function canonical(l: PivotSpec): PivotSpec {
  const n = normaliseLayout(l)
  const lists = (m: Record<string, string[]>) => Object.fromEntries(Object.entries(m).filter(([, v]) => v.length > 0).sort(([a], [b]) => a.localeCompare(b)))
  const date = n.date.preset === 'custom' ? { preset: n.date.preset, from: n.date.from ?? '', to: n.date.to ?? '' } : { preset: n.date.preset }
  return { ...n, keep: lists(n.keep), excl: lists(n.excl), date }
}

export function sameLayout(a: PivotSpec, b: PivotSpec): boolean {
  return JSON.stringify(canonical(a)) === JSON.stringify(canonical(b))
}

/* ---------- table ---------- */

/** rowSpan of the dimension cell at (row, dim): 0 when an earlier row already covers it. */
export function rowSpans(keys: readonly (readonly string[])[]): number[][] {
  return keys.map((key, ri) =>
    key.map((_, di) => {
      const prefix = (k: readonly string[] | undefined) => (k ? k.slice(0, di + 1).join('\u0001') : null)
      const mine = prefix(key)
      if (ri > 0 && prefix(keys[ri - 1]) === mine) return 0
      let span = 1
      while (ri + span < keys.length && prefix(keys[ri + span]) === mine) span++
      return span
    }),
  )
}

/** colSpan of each column-key cell on header level `level`: 0 where the cell to its left covers it. */
export function colSpans(keys: readonly (readonly string[])[], level: number): number[] {
  return keys.map((key, ci) => {
    const prefix = (k: readonly string[] | undefined) => (k ? k.slice(0, level + 1).join('\u0001') : null)
    const mine = prefix(key)
    if (ci > 0 && prefix(keys[ci - 1]) === mine) return 0
    let span = 1
    while (ci + span < keys.length && prefix(keys[ci + span]) === mine) span++
    return span
  })
}

/** "1 240", "4 min 12 s", "86.4%", or a dash for no figure. */
export function formatCell(value: CellValue | undefined, kind: MeasureKind = 'count'): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  if (kind === 'percent') return `${(value * 100).toFixed(1)}%`
  if (kind === 'duration') return formatSeconds(value)
  return Math.round(value).toLocaleString('en-GB').replace(/,/g, ' ')
}

export function formatSeconds(seconds: number): string {
  const s = Math.max(0, Math.round(seconds))
  if (s < 60) return `${s} s`
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  if (h > 0) return m > 0 ? `${h} h ${m} min` : `${h} h`
  return s % 60 > 0 ? `${m} min ${s % 60} s` : `${m} min`
}

export const SCREEN_ROWS = 400
/** The cell menu is offered while one page holds at most this many cells. */
export const CONTEXT_MENU_CELLS = 2_500

/** Two initials for a person's avatar. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '?'
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : (parts[0]?.[1] ?? ''))).toUpperCase()
}

/* ---------- dates ---------- */

export const DATE_PRESETS: { value: Exclude<DatePreset, 'custom'>; key: StaffKey }[] = [
  { value: 'today', key: 'pivot.date.today' },
  { value: 'yesterday', key: 'pivot.date.yesterday' },
  { value: 'last_7', key: 'pivot.date.last7' },
  { value: 'last_30', key: 'pivot.date.last30' },
  { value: 'this_week', key: 'pivot.date.thisWeek' },
  { value: 'last_week', key: 'pivot.date.lastWeek' },
  { value: 'this_month', key: 'pivot.date.thisMonth' },
  { value: 'last_month', key: 'pivot.date.lastMonth' },
  { value: 'this_and_last_month', key: 'pivot.date.twoMonths' },
  { value: 'last_12_weeks', key: 'pivot.date.weeks12' },
  { value: 'last_12_months', key: 'pivot.date.months12' },
]

export const DATE_GROUPS: { value: DateGroup; key: StaffKey }[] = [
  { value: 'day', key: 'pivot.group.day' },
  { value: 'week', key: 'pivot.group.week' },
  { value: 'month', key: 'pivot.group.month' },
  { value: 'quarter', key: 'pivot.group.quarter' },
  { value: 'year', key: 'pivot.group.year' },
]


/** "3 Oct 2026" from YYYY-MM-DD. */
export function dayLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return iso
  return `${d} ${monthName('en', m - 1, 'short')} ${y}`
}

/** "3 – 9 Oct 2026" or "28 Sep – 9 Oct 2026". */
export function rangeLabel(from: string, to?: string): string {
  if (!to || to === from) return dayLabel(from)
  const [fy, fm] = from.split('-')
  const [ty, tm] = to.split('-')
  if (fy === ty && fm === tm) return `${Number(from.slice(8))} – ${dayLabel(to)}`
  if (fy === ty) return `${dayLabel(from).replace(` ${fy}`, '')} – ${dayLabel(to)}`
  return `${dayLabel(from)} – ${dayLabel(to)}`
}

export function dateLabel(date: DateFilter, say: (k: StaffKey) => string): string {
  if (date.preset === 'custom' && date.from) return rangeLabel(date.from, date.to)
  const preset = DATE_PRESETS.find((p) => p.value === date.preset)
  return preset ? say(preset.key) : say('pivot.date.last30')
}

/** "just now", "5 min ago", "3 h ago", "2 d ago", then the date after a week. */
export function relativeTime(iso: string, now = new Date()): string {
  const then = new Date(iso)
  const seconds = Math.max(0, (now.getTime() - then.getTime()) / 1000)
  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)} h ago`
  if (seconds < 7 * 86_400) return `${Math.floor(seconds / 86_400)} d ago`
  return fullDate(iso)
}

export function fullDate(iso: string): string {
  const d = new Date(iso)
  return `${d.getDate()} ${monthName('en', d.getMonth(), 'short')} ${d.getFullYear()}`
}

/* ---------- schedules ---------- */

/** "Every day · 08:00 · Excel" from a schedule's frequency, send minute and format. */
export function scheduleWhen(s: { frequency: string; sendMinute: number; format: string }): string {
  const when = s.frequency === 'daily' ? 'Every day' : s.frequency === 'weekly' ? 'Every Monday' : 'Every 1st'
  const hh = String(Math.floor(s.sendMinute / 60)).padStart(2, '0')
  const mm = String(s.sendMinute % 60).padStart(2, '0')
  const format = s.format === 'xlsx' ? 'Excel' : s.format.toUpperCase()
  return `${when} · ${hh}:${mm} · ${format}`
}

/** "Name (copy)", then "(copy 2)" …, as the server names a duplicate. */
export function copyName(name: string, taken: readonly string[]): string {
  const base = name.replace(/ \(copy(?: \d+)?\)$/, '')
  for (let n = 1; n < 100; n++) {
    const candidate = `${base} (copy${n > 1 ? ` ${n}` : ''})`
    if (!taken.includes(candidate)) return candidate.slice(0, 60)
  }
  return `${base} (copy)`.slice(0, 60)
}
